import Game from '../../game';
import LinedButtonsMenu, { LinedButtonsMenuParams } from '../../ui/menu/linedButtonsMenu';
import Ship, { ShipParams } from './ship';
import { WeaponParams } from './weapon';
import ConstructionControl from '../../control/constructionControl';
import FollowOrder from '../../../class/order/followOrder';
import MoveToOrder from '../../../class/order/moveToOrder';

import { getCorrectAngle, getXByDistanceAndAngle, getYByDistanceAndAngle } from '../../../utils/geometry';
import { getIntermediateColor, RGBA } from '../../../utils/color';
import { Faction } from '../../../utils/faction';

import * as shipsConfig from '../../../config/ships.config';
import * as weaponsConfig from '../../../config/weapons.config';


export interface CapitalShipParams extends ShipParams {
  resource?: number;
}

export default class CapitalShip extends Ship implements CapitalShipParams {
  resource = 0;

  capital = true;
  menuParameters!: LinedButtonsMenuParams;
  tractorRange = 1000;
  storedShips: Ship[] = [];
  canJump = true;

  temporaryCanvas: HTMLCanvasElement;
  temporaryCtx!: CanvasRenderingContext2D;

  constructor(params: CapitalShipParams) {
    super(params);
    Game.instance.player.capitalShip = this;

    this.temporaryCanvas = document.createElement('canvas');
    this.temporaryCanvas.width = this.width;
    this.temporaryCanvas.height = this.height;
    const temporaryCtx = this.temporaryCanvas.getContext('2d');
    if (temporaryCtx) this.temporaryCtx = temporaryCtx;
  }

  update(modifier: number): CapitalShip {
    super.update(modifier);
    this.repairStoredShips(modifier);
    return this;
  }

  subDraw(): CanvasRenderingContext2D {
    super.subDraw();
    this.drawStoredShips();
    return this.context;
  }

  drawStoredShips() {
    if (this.storedShips.length === 0
      || this.faction !== Faction.PLAYER
      || !(this.isHovered() || this.isSelected())
    )
      return;

    const { context, temporaryCtx } = this;
    const maxDisplayWidth = this.width / 2;
    const lines: Ship[][] = this.storedShips.reduce((acc: Ship[][], cur: Ship) => {
      const currentLine = acc[acc.length - 1];
      const currentAccWidth = currentLine.reduce((acc2: number, cur2: Ship) => acc2 + cur2.width, 0);
      if (currentAccWidth < maxDisplayWidth) {
        currentLine.push(cur);
      } else {
        acc.push([cur]);
      }
      return acc;
    }, [[]] as Ship[][]);

    let currentLineNumber = 0;
    let paddingLeft = 0;
    let paddingTop = 0;

    for (const ship of this.storedShips) {
      const currentLineAccWidth = lines[currentLineNumber].reduce((acc, cur) => acc + cur.width, 0);
      const currentLineMaxHeight = Math.max(...lines[currentLineNumber].map(s => s.height));

      /* use a temporaryCtx as image to draw the colored shape of the ship */
      temporaryCtx.globalCompositeOperation = 'source-over';
      temporaryCtx.fillStyle = RGBA.toString(getIntermediateColor({
        startColor: { R: 0, G: 255, B: 0, A: 1 },
        endColor: { R: 255, G: 0, B: 0, A: 1 },
        ratio: ship.HP / ship.HPMax,
      }));
      temporaryCtx.fillRect(0, 0, this.width, this.height);
      temporaryCtx.globalCompositeOperation = 'destination-in';
      const image = new Image();
      image.src = ship.sprite.src;
      temporaryCtx.drawImage(
        image,
        0,
        0,
        ship.width,
        ship.height,
      );

      context.drawImage(
        this.temporaryCanvas,
        0,
        0,
        ship.width,
        ship.height,
        this.x - currentLineAccWidth / 2 + paddingLeft,
        this.y - currentLineMaxHeight / 2 + paddingTop,
        ship.width,
        ship.height,
      );

      paddingLeft += ship.width;
      if (paddingLeft > maxDisplayWidth) {
        paddingLeft = 0;
        paddingTop += Math.max(...lines[currentLineNumber].map(s => s.height));
        currentLineNumber++;
      }
    };
  }

  jumpOut(): void {
    super.jumpOut();

    /* Capitalship jumps = end of current level */
    const fadeOutDuration = 1000;
    this.scene?.music?.fadeToVolume(0, fadeOutDuration);
    setTimeout(() => {
      Game.instance.level.end();
    }, fadeOutDuration);
  }

  handleRecall() {
    if (this.storedShips.length > 0) {
      this.releaseShips();
    } else {
      this.recallShips()
    }
  }

  recallShips() {
    const ships = Object.values(this.getScene().elements)
      .filter(element => element.parentShip?.id === this.id)
      .map(element => element as Ship);

    ships
      .forEach((ship) => {
        const recallOrder = new FollowOrder({
          target: this,
          distanceToReach: ship.width,
          onDistanceReached: () => {
            if (!this?.isInScene) return;
            this.storeShip(ship);
          }
        });
        ship.behaviour.setOrder(recallOrder);
      });
  }

  releaseShips() {
    if (this.storedShips.length === 0) return;

    const leftShips = this.storedShips.filter((ship, index) => index % 2 === 0);
    const rightShips = this.storedShips.filter((ship, index) => index % 2 !== 0);

    const hanleShips = (side: 'left' | 'right') => {
      return (ship: Ship, index: number, array: Ship[]) => {
        const arcAngle = 90;
        const angleBetweenShips = arcAngle / (array.length + 1);
        const additionalAngle = (array.length === 1) ? 0 : getCorrectAngle(arcAngle / 2 - angleBetweenShips * (index + 1));
        const angle = side === 'left'
          ? getCorrectAngle(this.lookAngle + 90 + additionalAngle)
          : getCorrectAngle(this.lookAngle - 90 - additionalAngle)

        const distance = this.width;

        const moveOrder = new MoveToOrder({
          x: getXByDistanceAndAngle({ x: this.x, angle, distance }),
          y: getYByDistanceAndAngle({ y: this.y, angle, distance })
        });

        ship.scene = this.scene;
        ship.x = this.x;
        ship.y = this.y;
        ship.lookAngle = angle;
        ship.moveAngle = angle;
        ship.moveSpeed = 0;
        ship.behaviour.setOrder(moveOrder);
        ship.create();
      };
    }

    leftShips.forEach(hanleShips('left'));
    rightShips.forEach(hanleShips('right'));
    Game.instance.controlSchemes.rts.selectedElementsIds = this.storedShips.map(ship => ship.id); // QoL: automatically select the released ships
    this.storedShips = [];
  }

  storeShip(ship: Ship) {
    this.storedShips.push(ship);
    ship.remove();

    /* QoL: select the storing ship if all selected ships have been stored */
    if (Game.instance.controlSchemes.rts.getSelectedElements().length === 0) {
      Game.instance.controlSchemes.rts.selectedElementsIds = [this.id];
    }
  }

  repairStoredShips(modifier: number) {
    const repairSpeed = 10; // HP / s (TODO: dynamic)
    this.storedShips.forEach((ship) => {
      const repairCost = repairSpeed * modifier;
      const healedHP = repairSpeed * modifier;
      if (Game.instance.player.resources < repairCost || ship.HP === ship.HPMax) return;

      Game.instance.player.resources -= repairCost;
      ship.heal(healedHP);
    })
  }

  openMenu(): void {
    const ctx = Game.instance.contexts.foregroundCanvas;

    const constructionBaseparams = {
      context: ctx,
    };

    new LinedButtonsMenu({
      context: ctx,
      ignoreCamera: true,
      x: Game.instance.canvasWidth / 2,
      y: Game.instance.canvasHeight - 100,
      itemWidth: 50,
      height: 50,
      buttonsParameters:
        [
          {
            caption: 'Ships', onSelect: () => {
              new LinedButtonsMenu({
                context: ctx,
                ignoreCamera: true,
                x: Game.instance.canvasWidth / 2,
                y: Game.instance.canvasHeight - 100 - 50,
                itemWidth: 50,
                height: 50,
                buttonsParameters:
                  [
                    {
                      caption: 'Jet', onSelect: () => {
                        new ConstructionControl({
                          ...constructionBaseparams,
                          buildingParams: {
                            ...shipsConfig.basicJet,
                            parentShip: this,
                            building: {
                              built: false,
                              constructionTime: 1000,
                              constructionTimeDone: 0,
                            },
                          } as ShipParams,
                          buildingClass: 'Ship',
                        }).enable();
                      }
                    },
                  ]
              } as LinedButtonsMenuParams);
            }
          },
          {
            caption: 'Turrets', onSelect: () => {
              new LinedButtonsMenu({
                context: ctx,
                ignoreCamera: true,
                x: Game.instance.canvasWidth / 2,
                y: Game.instance.canvasHeight - 100 - 50,
                itemWidth: 50,
                height: 50,
                buttonsParameters:
                  [
                    {
                      caption: 'destroyerTurret', onSelect: () => {
                        new ConstructionControl({
                          ...constructionBaseparams,
                          buildingParams: {
                            ...weaponsConfig.destroyerTurret,
                            ship: this,
                            building: {
                              built: false,
                              constructionTime: 1000,
                              constructionTimeDone: 0,
                            },
                          } as WeaponParams,
                          buildingClass: 'Weapon',
                        }).enable();
                      }
                    },
                  ]
              } as LinedButtonsMenuParams);
            }
          },
        ]
    } as LinedButtonsMenuParams);
  }
}